import { useState, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ChartBottomBtn from 'components/pages/chart/ChartBottomBtn';
import Pentool from 'components/pages/chart/Pentool';
import ConfirmChartRegister from 'pages/modal/ConfirmChartRegister';
import StudyModelChart from '../StudyModelChart';
import {
  StudyModelChartFormDefaultValues,
  AttachmentFilesDefaultValues,
} from 'constants/chart/studyModelChart';
import {
  TStudyModelChartFormValues,
  TAttachmentFiles,
} from 'types/chart/studyModelChart';
import { TPracticeChartInfo } from 'types/apis/chart/studentPracticeChart';
import {
  TEvaluationChartInfo,
  TPutEvaluationChartResponse,
} from 'types/apis/chart/studentEvaluationChart';
import { TTool } from 'types/apis/chart/studentPracticeChart';
import { TLine } from 'types/chart/pentool';
import { TChartFileInfoList } from 'types/chart';
import useModal from 'hooks/useModal';
import { TChartInfoResponse } from 'hooks/chart/useGetChartInfo';
import useChartRegister from 'hooks/chart/useChartRegister';
import Konva from 'konva';

type TProps = {
  query: URLSearchParams;
  chartType: number;
  category: string;
  id: number;
  isProfessor: boolean;
  isRegister: boolean;
  setSubmitEndDate: (submitEndDate: string) => void;
  tool: TTool;
  setTool: (tool: TTool) => void;
  strokeActive: boolean;
  setStrokeActive: (active: boolean) => void;
  strokeWidth: number;
  setStrokeWidth: (strokeWidth: number) => void;
  strokeColor: string;
  setStrokeColor: (strokeColor: string) => void;
  chartInfoResponse: TChartInfoResponse | null;
  activeTabIndex: number;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isEdit: boolean;
  isReadOnly: boolean;
  checkSubmitEndDate: (submitEndDate: string) => void;
  handleSuccessGetChartInfo: (data: TChartInfoResponse) => void;
  setStageImage: (ref: any) => void;
};

const StudyModelChartRegister = ({
  query,
  chartType,
  category,
  id,
  isProfessor,
  isRegister,
  setSubmitEndDate,
  tool,
  setTool,
  strokeActive,
  setStrokeActive,
  strokeWidth,
  setStrokeWidth,
  strokeColor,
  setStrokeColor,
  chartInfoResponse,
  activeTabIndex,
  isLoading,
  setIsLoading,
  isEdit,
  isReadOnly,
  checkSubmitEndDate,
  handleSuccessGetChartInfo,
  setStageImage,
}: TProps) => {
  const { openModal } = useModal();

  const [attachmentFiles, setAttachmentFiles] = useState<TAttachmentFiles>(
    AttachmentFilesDefaultValues
  );
  const [deleteFileIdList, setDeleteFileIdList] = useState<number[]>([]);

  const [lines, setLines] = useState<TLine[]>([]);

  const { chartRegister } = useChartRegister();

  const methods = useForm<TStudyModelChartFormValues>({
    defaultValues: StudyModelChartFormDefaultValues,
  });
  const { handleSubmit, setValue, watch, reset } = methods;
  const formValues = watch();
  const formValuesRef = useRef(formValues);
  const deleteFileIdListRef = useRef(deleteFileIdList);
  const attachmentFilesRef = useRef(attachmentFiles);
  const prevFormValuesRef = useRef<TStudyModelChartFormValues | null>(null);
  const prevAttachmentFilesRef = useRef<TAttachmentFiles | null>(null);

  const stageRef = useRef<any>();
  const penLayerRef = useRef<any>();

  useEffect(() => {
    setValue(
      'drawing' as keyof TStudyModelChartFormValues,
      stageRef.current?.toJSON() as TStudyModelChartFormValues['drawing']
    );
    setStageImage(stageRef);
  }, [lines]);

  // 실습용 - 자동 저장 (20초 간격)
  useEffect(() => {
    if (isLoading) return;
    prevFormValuesRef.current = formValues;
    prevAttachmentFilesRef.current = attachmentFiles;
  }, [isLoading]);

  useEffect(() => {
    formValuesRef.current = formValues;
    deleteFileIdListRef.current = deleteFileIdList;
    attachmentFilesRef.current = attachmentFiles;
  }, [formValues, deleteFileIdList, attachmentFiles]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (!isLoading && isRegister && category === 'evaluation') {
      intervalId = setInterval(() => {
        if (isReadOnly) return;
        // 초기 상태 자동 저장 방지
        if (
          (chartInfoResponse?.chartInfo as TEvaluationChartInfo[])[
            activeTabIndex > 1 ? 1 : 0
          ]?.chartData === null &&
          JSON.stringify(StudyModelChartFormDefaultValues) ===
            JSON.stringify(formValuesRef.current)
        )
          return;
        // 이전 값과 비교하여 변경된 경우만 자동 저장 요청
        if (
          JSON.stringify(formValuesRef.current) !==
            JSON.stringify(prevFormValuesRef.current) ||
          JSON.stringify(attachmentFilesRef.current) !==
            JSON.stringify(prevAttachmentFilesRef.current)
        ) {
          onValid(
            true,
            formValuesRef.current,
            deleteFileIdListRef.current,
            attachmentFilesRef.current
          );
        }
      }, 20000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRegister, isLoading, category, isReadOnly]);

  const setChartInfo = (
    chartInfo: TPracticeChartInfo | TEvaluationChartInfo
  ) => {
    const chartData: TStudyModelChartFormValues | null =
      (chartInfo?.chartData as TStudyModelChartFormValues | null) || null;

    if (category !== 'practice') {
      const submitEndDate =
        (chartInfo as TEvaluationChartInfo)?.submitEndDate || '';
      setSubmitEndDate(submitEndDate);
      checkSubmitEndDate(submitEndDate);
    }
    if (!chartData) {
      reset();
      setFiles([], false);
      setIsLoading(false);
      return;
    }
    Object.keys(chartData).forEach((key) => {
      setValue(
        key as keyof TStudyModelChartFormValues,
        chartData[key as keyof TStudyModelChartFormValues]
      );
    });

    setFiles(
      chartInfo?.fileInfoList?.sort(
        (a, b) => a.fileArrangeNum - b.fileArrangeNum
      ) || [],
      false
    );

    if (!chartData.drawing) {
      setIsLoading(false);
      return;
    }

    const drawingData = JSON.parse(chartData.drawing as string);

    // 데이터에서 라인을 하나라도 가지고 있는 레이어가 있다면 추출
    const includeLineLayers = drawingData.children.filter((layer: any) => {
      return layer.children.some(
        (layerChild: any) => layerChild.className === 'Line'
      );
    });

    const newLines: TLine[] = [];

    includeLineLayers.forEach((lineLayer: any) => {
      lineLayer.children.forEach((lineLayerChild: any) => {
        if (lineLayerChild.className !== 'Line') {
          return;
        }
        // 각 라인으로 새로운 라인 만들기
        const newLine = new Konva.Line(lineLayerChild);

        // 라인의 attrs 에서 points 만 추출
        newLines.push({
          points: newLine.attrs.points,
          strokeColor: newLine.attrs.stroke,
          strokeWidth: newLine.attrs.strokeWidth,
          tool: !newLine.attrs.globalCompositeOperation ? 'pen' : 'eraser',
        });
      });
    });
    // 라인의 attrs 에서 얻은 points 로 state 변경하기
    setLines(newLines);
    setIsLoading(false);
  };

  const setFiles = (
    fileInfoList: TChartFileInfoList[],
    isAutoSave: boolean
  ) => {
    const maxArrangeNum = Math.max(
      ...fileInfoList.map((file) => file.fileArrangeNum)
    );
    const newAttachmentFiles = Array.from({ length: maxArrangeNum }, (_, i) => {
      const arrangeNum = i + 1;
      const fileInfo = fileInfoList.find(
        (file) => file.fileArrangeNum === arrangeNum
      );

      if (fileInfo) {
        return new File([], fileInfo.filePath, {
          type: `fileid:${fileInfo.fileId}`,
        });
      } else {
        return null;
      }
    });
    setAttachmentFiles(newAttachmentFiles);
    if (isAutoSave) prevAttachmentFilesRef.current = newAttachmentFiles;
  };

  const changeFile = (index: number, file: File[]) =>
    setAttachmentFiles((prev) => {
      const newFiles = { ...prev };
      newFiles[index] = file[0] || null;
      return newFiles;
    });

  // 차트 조회
  const formMediaFileRefs = useRef<Array<{ resetFile: () => void } | null>>([]);

  useEffect(() => {
    if (!chartInfoResponse) return;
    const chartInfo =
      category === 'practice'
        ? (chartInfoResponse.chartInfo as TPracticeChartInfo)
        : (chartInfoResponse.chartInfo as TEvaluationChartInfo[])[
            activeTabIndex > 1 ? 1 : 0
          ];
    setIsLoading(true);
    formMediaFileRefs.current.forEach((ref) => {
      if (ref) {
        ref.resetFile();
      }
    });
    setChartInfo(chartInfo);
  }, [activeTabIndex]);

  useEffect(() => {
    if (chartInfoResponse) {
      handleSuccessGetChartInfo(chartInfoResponse);
    }
  }, []);

  // 차트 등록
  const onValid = (
    isAutoSave: boolean,
    data: TStudyModelChartFormValues,
    deleteFileIdListData: number[] = deleteFileIdList,
    attachmentFilesData: TAttachmentFiles = attachmentFiles
  ) => {
    const formData = new FormData();

    formData.append(
      'request',
      new Blob(
        [
          JSON.stringify({
            chartType,
            chartData: data,
            deleteFileIdList: deleteFileIdListData,
          }),
        ],
        {
          type: 'application/json',
        }
      )
    );
    for (let i = 0; i < 5; i++) {
      if (
        !!attachmentFilesData[i] &&
        !attachmentFilesData[i]?.type.includes('fileid')
      ) {
        formData.append(`file${i + 1}`, attachmentFilesData[i] as Blob);
      }
    }
    chartRegister({
      category,
      id: id,
      data: formData,
      isEdit: isEdit,
      isAutoSave,
      patientId: query.get('patientId') || undefined,
      courseInfoId: query.get('courseInfoId') || undefined,
      chartRound: query.get('chartRound') || undefined,
      onLoading: setIsLoading,
      onConfirm: () => {
        openModal({
          element: (
            <ConfirmChartRegister
              category={category}
              chartType={chartType}
              id={id}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              chartName={chartInfoResponse?.chartName}
              data={formData}
            />
          ),
        });
      },
    });
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit((data) => onValid(false, data))}>
          {!isProfessor && isRegister && (
            <Pentool
              setTool={setTool}
              strokeActive={strokeActive}
              setStrokeActive={setStrokeActive}
              setStrokeWidth={setStrokeWidth}
              setStrokeColor={setStrokeColor}
              isRegister={
                (!isProfessor && isRegister && !isReadOnly) ||
                (isProfessor && !isRegister && isEdit)
              }
              feedbackLines={lines}
              setFeedbackLines={setLines}
            />
          )}
          <StudyModelChart
            formMediaFileRefs={formMediaFileRefs}
            attachmentFiles={attachmentFiles}
            deleteFileIdList={deleteFileIdList}
            setDeleteFileIdList={setDeleteFileIdList}
            changeFile={changeFile}
            tool={tool}
            strokeWidth={strokeWidth}
            strokeColor={strokeColor}
            strokeActive={strokeActive}
            lines={lines}
            setLines={setLines}
            stageRef={stageRef}
            penLayerRef={penLayerRef}
            isRegister={isRegister}
            isReadOnly={isReadOnly}
          />
          {(Number(query.get('tab')) === 0 ||
            Number(query.get('tab')) === 2) && (
            <ChartBottomBtn
              category={category}
              id={id}
              courseId={query.get('courseId') || undefined}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              studentUserId={query.get('studentUserId') || undefined}
              isRegister={isRegister}
              isEdit={isEdit}
              isReadOnly={isReadOnly}
              isChart={true}
              chartName={chartInfoResponse?.chartName}
            />
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default StudyModelChartRegister;
